/**
 * Legacy decryption-failure repair task.
 *
 * Background:
 * - Older versions of the app persisted the failure-sentinel string
 *   ("Could not decrypt message") into `MessageContentEntity.text` whenever
 *   decryption threw. The same sentinel was copied into the denormalized
 *   `ChatEntity.lastMessage` row that drives the chat-list preview.
 * - Once written, the row was never re-attempted, so users saw the sentinel
 *   forever even though the actual ciphertext was perfectly fine.
 *
 * What this module does:
 * - Scans `messages` and `chats.lastMessage` once per client and rewrites
 *   any row matching the legacy bad shape so it gets `decryptionStatus:
 *   "failed"` and an empty content payload. The retry orchestrator then
 *   picks up the row exactly like a fresh failure.
 *
 * Constraints:
 * - Runs OUTSIDE the IDB upgrade transaction (the upgrade itself only adds
 *   optional fields; data fixes go here).
 * - Chunked so a user with thousands of messages doesn't block the main
 *   thread for seconds.
 * - Idempotent: gated by a localStorage flag so it runs at most once. Even
 *   without the flag, re-running cannot corrupt clean rows because the
 *   structural match is precise.
 * - Failures here must never crash the app: wrapped in try/catch.
 */

import { db } from "@/indexedDB/db";
import { ChatEntity, MessageEntity } from "@/indexedDB/types";
import { createLogger } from "@/utils/debug";
import { DECRYPTION_FAILURE_SENTINEL } from "./decryptionError";
import { featureFlags } from "@/config/featureFlags";
import { safeStorage } from "@/utils/safeStorage";

const log = createLogger("LegacyDecryptionRepair");

const REPAIR_FLAG_KEY = "legacyDecryptionRepairCompleted";
const CHUNK_SIZE = 100;

/**
 * Structural check for the legacy bad shape: text matches the sentinel AND
 * none of the other content sub-fields are set.
 *
 * Acknowledged false-positive: a legitimate plaintext message whose text is
 * EXACTLY the sentinel string ("Could not decrypt message") with no
 * attachment/link/voice would also match and get rewritten to `{ text: "" }`
 * + flagged failed. The retry orchestrator would then refetch the
 * ciphertext from the server and decrypt successfully — restoring the
 * original text. The user-facing impact is at most a brief "Decrypting…"
 * flicker on that one message. We accept this tradeoff because (a) the
 * probability of a real message being byte-exact to the sentinel is
 * vanishingly small, and (b) the alternative — leaving any sentinel-text
 * row untouched — strands every actual legacy bad row forever.
 */
const isLegacyFailureContent = (
  content: MessageEntity["content"] | undefined,
): boolean => {
  if (!content) {
    return false;
  }
  return (
    content.text === DECRYPTION_FAILURE_SENTINEL &&
    !content.link &&
    !content.media &&
    !content.file &&
    !content.voice
  );
};

/**
 * In-memory yield helper. We use a 0ms timeout so each chunk is split across
 * macrotasks, leaving room for UI input handling.
 */
const yieldToMainThread = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

const repairMessagesTable = async (): Promise<number> => {
  let totalRepaired = 0;
  // Cursor-based pagination over the primary key (`_clientId`). Avoids the
  // O(n²) cost of Dexie's `.offset(n)` (which has to walk all skipped rows
  // each chunk) — important for users with very large `messages` tables.
  let lastKey: string | null = null;

  for (;;) {
    // Explicit type annotation breaks the TS7022 self-reference that arises
    // because the two ternary branches infer mutually-referential types.
    const query: ReturnType<typeof db.messages.orderBy> =
      lastKey === null
        ? db.messages.orderBy("_clientId")
        : db.messages.where("_clientId").above(lastKey);
    const chunk: MessageEntity[] = await query.limit(CHUNK_SIZE).toArray();
    if (chunk.length === 0) {
      break;
    }

    const repairs: Promise<unknown>[] = [];
    for (const msg of chunk) {
      if (isLegacyFailureContent(msg.content)) {
        repairs.push(
          db.messages.update(msg._clientId, {
            content: { text: "" },
            decryptionStatus: "failed",
            decryptionAttempts: 0,
          } satisfies Partial<MessageEntity>),
        );
        totalRepaired += 1;
      }
    }

    if (repairs.length > 0) {
      await Promise.all(repairs);
    }

    lastKey = chunk[chunk.length - 1]._clientId;
    if (chunk.length < CHUNK_SIZE) {
      break;
    }
    await yieldToMainThread();
  }

  return totalRepaired;
};

const repairChatLastMessages = async (): Promise<number> => {
  let totalRepaired = 0;
  let lastKey: string | null = null;

  for (;;) {
    // Explicit type annotation breaks the TS7022 self-reference that arises
    // because the two ternary branches infer mutually-referential types.
    const query: ReturnType<typeof db.chats.orderBy> =
      lastKey === null
        ? db.chats.orderBy("id")
        : db.chats.where("id").above(lastKey);
    const chunk: ChatEntity[] = await query.limit(CHUNK_SIZE).toArray();
    if (chunk.length === 0) {
      break;
    }

    const repairs: Promise<unknown>[] = [];
    for (const chat of chunk) {
      const lm = chat.lastMessage;
      if (lm && isLegacyFailureContent(lm.content)) {
        const repaired: MessageEntity = {
          ...lm,
          content: { text: "" },
          decryptionStatus: "failed",
          decryptionAttempts: 0,
        };
        repairs.push(
          db.chats.update(chat.id, {
            lastMessage: repaired,
          } satisfies Partial<ChatEntity>),
        );
        totalRepaired += 1;
      }
    }

    if (repairs.length > 0) {
      await Promise.all(repairs);
    }

    lastKey = chunk[chunk.length - 1].id;
    if (chunk.length < CHUNK_SIZE) {
      break;
    }
    await yieldToMainThread();
  }

  return totalRepaired;
};

/**
 * Run the legacy-repair pass. Safe to call multiple times — the
 * localStorage flag short-circuits subsequent runs. Errors are swallowed
 * after logging.
 *
 * Returns `{ ran, repairedMessages, repairedChats }` for tests.
 */
export const runLegacyDecryptionRepair = async (): Promise<{
  ran: boolean;
  repairedMessages: number;
  repairedChats: number;
}> => {
  if (!featureFlags.decryptionRetry) {
    return { ran: false, repairedMessages: 0, repairedChats: 0 };
  }

  if (safeStorage.get(REPAIR_FLAG_KEY) === "1") {
    return { ran: false, repairedMessages: 0, repairedChats: 0 };
  }

  try {
    const [repairedMessages, repairedChats] = await Promise.all([
      repairMessagesTable(),
      repairChatLastMessages(),
    ]);

    log.info("Legacy decryption repair complete", {
      repairedMessages,
      repairedChats,
    });

    // Storage write failure is non-fatal (safeStorage no-ops); a next run repeats.
    safeStorage.set(REPAIR_FLAG_KEY, "1");

    return { ran: true, repairedMessages, repairedChats };
  } catch (error) {
    log.error("Legacy decryption repair failed", error);
    return { ran: false, repairedMessages: 0, repairedChats: 0 };
  }
};

// Test-only helpers.
export const _LEGACY_REPAIR_FLAG_KEY = REPAIR_FLAG_KEY;
export const _isLegacyFailureContent = isLegacyFailureContent;
